import { Transforms } from 'slate';

import { type Editor } from '@@editor/helpers';
import { Element, ELEMENT_TYPES } from '@@editor/helpers/Element';
import deleteBackward from '@@editor/plugins/utils/deleteBackward';
import deleteForward from '@@editor/plugins/utils/deleteForward';
import deleteFragment from '@@editor/plugins/utils/deleteFragment';
import {
    normalizeInlineEditableElement,
    preventDeleteBackward,
    preventDeleteForward,
    preventDeleteFragment,
    preventInsertBreak,
} from '@@editor/plugins/utils/inlineEditing';
import insertBreak from '@@editor/plugins/utils/insertBreak';
import normalizeNode from '@@editor/plugins/utils/normalizeNode';
import renderEditor from '@@editor/plugins/utils/renderEditor';
import renderElement from '@@editor/plugins/utils/renderElement';
import { createElement } from '@@editor/serialization/UnitySerializer/deserializeNodes';

import EditorWithEmbedModal from '../embed/components/EditorWithEmbedModal';
import { createGenerateLayoutBlock } from '../layout/utils';
import QuestionAnswersForm from './../layout/components/QuestionAnswersForm';
import PollAnswerElement from './components/PollAnswerElement';
import PollElement, {
    INLINE_EDITABLE_CHILDREN_TYPES,
    NODE_TYPE,
    TYPE,
} from './components/PollElement';
import PollQuestionElement from './components/PollQuestionElement';
import { removePollId } from './utils';

export const generateLayoutBlock = createGenerateLayoutBlock({
    type: TYPE,
    nodeType: NODE_TYPE,
    children: [
        createElement(ELEMENT_TYPES.POLL_QUESTION),
        createElement(ELEMENT_TYPES.POLL_ANSWER),
    ],
});

const internalWithPoll = (editor: Editor, options) => {
    const { apply } = editor;

    return Object.assign(editor, {
        apply: (operation) => {
            // By default slate is splitting an existing node when inserting a break (newline),
            // which also means, data is copied from the existing node to the new one. Therefore we
            // need to reset the poll-id for the new, at this place selected, node (BE will generate
            // a new id for the new node during save)
            if (
                operation.type === 'split_node' &&
                Element.isPollAnswerElement(operation.properties)
            ) {
                apply({
                    ...operation,
                    properties: removePollId(operation.properties),
                });

                return;
            }

            apply(operation);
        },
        deleteForward: deleteForward(editor, [
            [preventDeleteForward, { types: INLINE_EDITABLE_CHILDREN_TYPES }],
        ]),
        deleteBackward: deleteBackward(editor, [
            [preventDeleteBackward, { types: INLINE_EDITABLE_CHILDREN_TYPES }],
        ]),
        deleteFragment: deleteFragment(editor, [
            [preventDeleteFragment, { types: INLINE_EDITABLE_CHILDREN_TYPES }],
        ]),
        insertBreak: insertBreak(editor, [
            [preventInsertBreak, { types: [ELEMENT_TYPES.INTERVIEW_SEGMENT_QUESTION] }],
        ]),
        normalizeNode: normalizeNode(editor, [
            [
                normalizeInlineEditableElement,
                {
                    type: ELEMENT_TYPES.POLL,
                    allowedChildrenTypes: INLINE_EDITABLE_CHILDREN_TYPES,
                },
            ],
        ]),
        renderEditor: renderEditor(
            editor,
            (props) => (
                <EditorWithEmbedModal
                    {...props}
                    formProps={{ editor, formName: 'LayoutPoll' }}
                    formComponent={QuestionAnswersForm}
                    generateEmbedBlock={generateLayoutBlock}
                    type={TYPE}
                />
            ),
            options,
        ),
        renderElement: renderElement(
            editor,
            [
                [NODE_TYPE, PollElement],
                [ELEMENT_TYPES.POLL_QUESTION, PollQuestionElement],
                [ELEMENT_TYPES.POLL_ANSWER, PollAnswerElement],
            ],
            options,
        ),
        insertPoll: (at = editor.selection?.focus) => {
            generateLayoutBlock(editor, undefined, {
                at,
                replace: false,
            });
            if (at) {
                Transforms.select(editor, at);
                Transforms.collapse(editor);
            }
        },
    });
};

export const withPoll = (editor, options) =>
    internalWithPoll(editor, {
        ...options,
        nodeType: NODE_TYPE,
    });

export default withPoll;
